import React, { Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from '@/context/AuthContext'
import { CartProvider } from '@/context/CartContext'
import { ProductProvider } from '@/context/ProductContext'
import { UIProvider } from '@/context/UIContext'
import { AdminProvider } from '@/context/AdminContext'
import Layout from '@/components/Layout/Layout'
import Loading from '@/components/UI/Loading'
import ErrorBoundary from '@/pages/Error/ErrorBoundary'

// Lazy load pages for code splitting
const Home = React.lazy(() => import('@/pages/Home'))
const About = React.lazy(() => import('@/pages/About'))
const Categories = React.lazy(() => import('@/pages/Categories'))
const ProductCatalog = React.lazy(() => import('@/pages/Products/ProductCatalog'))
const ProductDetail = React.lazy(() => import('@/pages/Products/ProductDetail'))
const ProductsByCategory = React.lazy(() => import('@/pages/Products/ProductsByCategory'))
const SearchResults = React.lazy(() => import('@/pages/Products/SearchResults'))
const Cart = React.lazy(() => import('@/pages/Cart/SimpleCart'))
const Checkout = React.lazy(() => import('@/pages/Cart/Checkout'))
const OrderConfirmation = React.lazy(() => import('@/pages/Cart/OrderConfirmation'))
const Login = React.lazy(() => import('@/pages/User/Login'))
const Register = React.lazy(() => import('@/pages/User/Register'))
const ForgotPassword = React.lazy(() => import('@/pages/User/ForgotPassword'))
const Profile = React.lazy(() => import('@/pages/User/Profile'))
const Orders = React.lazy(() => import('@/pages/User/Orders'))
const Wishlist = React.lazy(() => import('@/pages/User/Wishlist'))
const Contact = React.lazy(() => import('@/pages/Info/Contact'))
const FAQ = React.lazy(() => import('@/pages/Info/FAQ'))
const Delivery = React.lazy(() => import('@/pages/Info/Delivery'))
const Returns = React.lazy(() => import('@/pages/Info/Returns'))
const Privacy = React.lazy(() => import('@/pages/Info/Privacy'))
const Terms = React.lazy(() => import('@/pages/Info/Terms'))
const NotFound = React.lazy(() => import('@/pages/Error/NotFound'))

// Admin pages
const AdminLogin = React.lazy(() => import('@/admin/pages/AdminLogin'))
const AdminDashboard = React.lazy(() => import('@/admin/pages/MinimalAdminDashboard'))
const ProductList = React.lazy(() => import('@/admin/pages/Products/ProductList'))
const AddProduct = React.lazy(() => import('@/admin/pages/Products/EnhancedAddProduct'))
const EditProduct = React.lazy(() => import('@/admin/pages/Products/EditProduct'))
const BulkUpload = React.lazy(() => import('@/admin/pages/Products/BulkUpload'))
const OrderList = React.lazy(() => import('@/admin/pages/Orders/OrderList'))
const OrderDetail = React.lazy(() => import('@/admin/pages/Orders/OrderDetail'))
const OrderStatus = React.lazy(() => import('@/admin/pages/Orders/OrderStatus'))
const CategoryList = React.lazy(() => import('@/admin/pages/Categories/SimpleCategoryManager'))
const AddCategory = React.lazy(() => import('@/admin/pages/Categories/AddCategory'))
const ProductManager = React.lazy(() => import('@/admin/pages/Products/ProductManager'))
const UserList = React.lazy(() => import('@/admin/pages/Users/UserList'))
const UserDetail = React.lazy(() => import('@/admin/pages/Users/UserDetail'))
const SalesReport = React.lazy(() => import('@/admin/pages/Reports/SalesReport'))
const ProductReport = React.lazy(() => import('@/admin/pages/Reports/ProductReport'))
const UserReport = React.lazy(() => import('@/admin/pages/Reports/UserReport'))
const SiteSettings = React.lazy(() => import('@/admin/pages/Settings/SiteSettings'))
const PaymentSettings = React.lazy(() => import('@/admin/pages/Settings/PaymentSettings'))
const DeliverySettings = React.lazy(() => import('@/admin/pages/Settings/ShippingSettings'))

// Admin components
import ProtectedRoute from '@/components/Admin/ProtectedRoute'

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ProductProvider>
          <CartProvider>
            <UIProvider>
              <AdminProvider>
                <Layout>
                  <Suspense fallback={<Loading />}>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/categories" element={<Categories />} />
                      <Route path="/products" element={<ProductCatalog />} />
                      <Route path="/products/:id" element={<ProductDetail />} />
                      <Route path="/category/:slug" element={<ProductsByCategory />} />
                      <Route path="/search" element={<SearchResults />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/delivery" element={<Delivery />} />
                      <Route path="/returns" element={<Returns />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      
                      {/* Protected User Routes */}
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/orders" element={<Orders />} />
                      <Route path="/wishlist" element={<Wishlist />} />
                      
                      {/* Admin Routes */}
                      <Route path="/admin/login" element={<AdminLogin />} />
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="/admin/products" element={
                        <ProtectedRoute>
                          <ProductList />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/products/add" element={
                        <ProtectedRoute>
                          <AddProduct />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/products/edit/:id" element={
                        <ProtectedRoute>
                          <EditProduct />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/products/bulk-upload" element={
                        <ProtectedRoute>
                          <BulkUpload />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/orders" element={
                        <ProtectedRoute>
                          <OrderList />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/orders/:id" element={
                        <ProtectedRoute>
                          <OrderDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/orders/:id/status" element={
                        <ProtectedRoute>
                          <OrderStatus />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/categories" element={<CategoryList />} />
                      <Route path="/admin/categories/add" element={
                        <ProtectedRoute>
                          <AddCategory />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/product-manager" element={
                        <ProtectedRoute>
                          <ProductManager />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/users" element={
                        <ProtectedRoute>
                          <UserList />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/users/:id" element={
                        <ProtectedRoute>
                          <UserDetail />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/customers" element={
                        <ProtectedRoute>
                          <UserList />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/reports" element={
                        <ProtectedRoute>
                          <SalesReport />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/reports/sales" element={
                        <ProtectedRoute>
                          <SalesReport />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/reports/products" element={
                        <ProtectedRoute>
                          <ProductReport />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/reports/users" element={
                        <ProtectedRoute>
                          <UserReport />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/settings" element={
                        <ProtectedRoute>
                          <SiteSettings />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/settings/site" element={
                        <ProtectedRoute>
                          <SiteSettings />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/settings/payment" element={
                        <ProtectedRoute>
                          <PaymentSettings />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/settings/delivery" element={
                        <ProtectedRoute>
                          <DeliverySettings />
                        </ProtectedRoute>
                      } />
                      
                      {/* 404 Route */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </Layout>
              </AdminProvider>
            </UIProvider>
          </CartProvider>
        </ProductProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
